import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useLanguage } from '../../context/LanguageContext';

const { 
  FiShare2, FiClock, FiSliders, FiVideo, FiShield, FiLayout,
  FiCheckCircle, FiSend, FiImage, FiZap, FiCheck
} = FiIcons;

const BifrostTab = () => {
  const { t } = useLanguage();
  const [selectedNetworks, setSelectedNetworks] = useState([
    'Instagram', 'LinkedIn', 'YouTube', 'TikTok', 'Threads', 'Facebook'
  ]);
  const [isPublished, setIsPublished] = useState(false);

  const features = [
    {
      icon: FiShare2,
      title: t('solutionsPage.bifrost.features.f1Title') || "Native Multi-Posting",
      description: t('solutionsPage.bifrost.features.f1Desc') || "Reach Instagram, TikTok, LinkedIn, Facebook, YouTube, Threads, and 7 more networks simultaneously with native-formatted content."
    },
    {
      icon: FiClock,
      title: t('solutionsPage.bifrost.features.f2Title') || "Intelligence Scheduling",
      description: t('solutionsPage.bifrost.features.f2Desc') || "Execute your social strategy in advance. Schedule posts for peak engagement times across different time zones automatically."
    },
    {
      icon: FiSliders,
      title: t('solutionsPage.bifrost.features.f3Title') || "Auto-Transformation",
      description: t('solutionsPage.bifrost.features.f3Desc') || "Bifrost detects platform requirements and automatically adjusts media sizes, caption lengths, and hashtag rules for perfect quality."
    },
    {
      icon: FiVideo,
      title: t('solutionsPage.bifrost.features.f4Title') || "Total Media Freedom",
      description: t('solutionsPage.bifrost.features.f4Desc') || "Full support for every format: High-res images, 4K video, Reels, TikToks, Carousels, and punchy text-only updates."
    },
    {
      icon: FiShield,
      title: t('solutionsPage.bifrost.features.f5Title') || "Quality Assurance",
      description: t('solutionsPage.bifrost.features.f5Desc') || "Stop worrying about blurry uploads. We maintain the highest bitrates and resolution standards across every single platform."
    },
    {
      icon: FiLayout,
      title: t('solutionsPage.bifrost.features.f6Title') || "Unified Interface",
      description: t('solutionsPage.bifrost.features.f6Desc') || "Just write your post, pick where it goes, and watch it go live. No more jumping between 13 different apps and logins."
    }
  ];

  const allNetworks = [
    { name: 'Instagram', tag: 'Reels / Post' },
    { name: 'TikTok', tag: 'Shorts' },
    { name: 'LinkedIn', tag: 'Professional' },
    { name: 'YouTube', tag: 'Video & Community' },
    { name: 'Facebook', tag: 'Page & Feed' },
    { name: 'Threads', tag: 'Text & Media' },
    { name: 'X (Twitter)', tag: 'Microblog' },
    { name: 'Pinterest', tag: 'Visual Pins' },
    { name: 'Reddit', tag: 'Community Hub' },
    { name: 'Bluesky', tag: 'Open Social' },
    { name: 'Mastodon', tag: 'Fediverse' },
    { name: 'Telegram', tag: 'Direct Broadcast' },
    { name: 'Discord', tag: 'Server Webhook' }
  ];

  const toggleNetwork = (name) => {
    setSelectedNetworks(prev => 
      prev.includes(name) 
        ? prev.filter(n => n !== name)
        : [...prev, name]
    );
  };

  const handlePublish = () => {
    setIsPublished(true);
    setTimeout(() => setIsPublished(false), 3000);
  };

  return (
    <div className="space-y-12 py-2">
      {/* Hero Header */}
      <div className="text-center max-w-4xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 backdrop-blur-md border border-white/60 text-pink-700 text-xs font-semibold uppercase tracking-wider shadow-sm inner-glow">
          <SafeIcon icon={FiShare2} className="w-3.5 h-3.5 text-pink-600 animate-pulse" />
          <span>{t('solutionsPage.bifrost.badge') || "13-Network Social Distribution"}</span>
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
          {t('solutionsPage.bifrost.title') || "Publish Across 13 Networks in One Click"}
        </h2>
        <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
          {t('solutionsPage.bifrost.subtitle') || "The social bridge. Publish content to 13 networks simultaneously through a single email draft."}
        </p>
      </div>

      {/* Interactive Multi-Posting Simulator */}
      <div className="glass-panel rounded-[28px] p-6 md:p-8 border border-white/80 shadow-xl shadow-slate-200/40 inner-glow relative overflow-hidden space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-5 border-b border-slate-200/70">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="w-2.5 h-2.5 rounded-full bg-pink-500 animate-pulse" />
              <h3 className="text-lg font-bold text-slate-900 tracking-tight">{t('solutionsPage.bifrost.studioTitle') || "Bifrost Omni-Channel Broadcast Studio"}</h3>
            </div>
            <p className="text-xs text-slate-500 mt-0.5 font-medium">{t('solutionsPage.bifrost.studioSubtitle') || "Select target channels to preview automatic aspect ratio and formatting adaptation."}</p>
          </div>
          <span className="px-3.5 py-1 rounded-full bg-pink-50 text-pink-700 border border-pink-200 text-xs font-bold font-mono">
            {selectedNetworks.length} / 13 {t('solutionsPage.bifrost.selectedCount') || "Networks Selected"}
          </span>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          {/* Post Composer (6 Cols) */}
          <div className="lg:col-span-6 space-y-4">
            <div className="p-5 rounded-2xl bg-white/80 border border-slate-200/80 shadow-sm space-y-4">
              <div className="flex items-center justify-between text-xs pb-2 border-b border-slate-100">
                <span className="font-bold text-slate-800">{t('solutionsPage.bifrost.draftTitle') || "Omni-Post Draft"}</span>
                <span className="text-emerald-600 font-semibold">{t('solutionsPage.bifrost.autoReady') || "Auto-Transformation Ready"}</span>
              </div>

              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/70 text-xs text-slate-800 leading-relaxed font-sans">
                {t('solutionsPage.bifrost.samplePost') || "🚀 Excited to announce our 2026 Keynote Speaker lineup! From AI breakthroughs to spatial events, get ready for an unforgettable experience. Grab early bird passes now 👇 #EventTech #Innovation2026"}
              </div>

              {/* Media attached */}
              <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-100/70 text-xs text-slate-600">
                <SafeIcon icon={FiImage} className="w-4 h-4 text-pink-600" />
                <span className="font-medium">{t('solutionsPage.bifrost.mediaAttached') || "4K Keynote Banner & 9:16 Video Clip Attached"}</span>
              </div>

              {/* Publish Action */}
              <button
                onClick={handlePublish}
                disabled={selectedNetworks.length === 0}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-pink-600 to-rose-600 text-white font-bold text-xs shadow-md shadow-pink-500/20 hover:shadow-lg transition-all flex items-center justify-center gap-2"
              >
                {isPublished ? (
                  <>
                    <SafeIcon icon={FiCheck} className="w-4 h-4" />
                    <span>{t('solutionsPage.bifrost.broadcasting') || "Broadcasting across Selected Networks..."}</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiSend} className="w-4 h-4" />
                    <span>{t('solutionsPage.bifrost.publishBtn') || `Publish to ${selectedNetworks.length} Selected Networks`}</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* 13 Network Selector Matrix (6 Cols) */}
          <div className="lg:col-span-6 space-y-3">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
              {t('solutionsPage.bifrost.supportedTitle') || "The 13 Supported Social Networks"}
            </span>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {allNetworks.map((net) => {
                const isSelected = selectedNetworks.includes(net.name);
                return (
                  <button
                    key={net.name}
                    onClick={() => toggleNetwork(net.name)}
                    className={`p-2.5 rounded-xl border text-left transition-all flex flex-col justify-between ${
                      isSelected
                        ? 'bg-white border-pink-500 ring-1 ring-pink-500/20 shadow-xs'
                        : 'bg-white/50 border-slate-200/80 hover:bg-white text-slate-500'
                    }`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className={`text-xs font-bold ${isSelected ? 'text-slate-900' : 'text-slate-600'}`}>
                        {net.name}
                      </span>
                      <div className={`w-3.5 h-3.5 rounded flex items-center justify-center text-[9px] font-bold ${
                        isSelected ? 'bg-pink-600 text-white' : 'border border-slate-300'
                      }`}>
                        {isSelected && '✓'}
                      </div>
                    </div>
                    <span className="text-[10px] text-slate-400 mt-1">{net.tag}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* The 6 Core Features Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.map((feat, idx) => (
          <motion.div
            key={idx}
            whileHover={{ y: -3 }}
            className="glass-card rounded-2xl p-6 border border-white/80 shadow-md shadow-slate-100/60 hover:shadow-xl hover:border-pink-200/80 transition-all flex flex-col justify-between"
          >
            <div className="space-y-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500/10 to-rose-600/20 border border-pink-200/60 flex items-center justify-center text-pink-600 shadow-sm">
                <SafeIcon icon={feat.icon} className="w-6 h-6" />
              </div>
              <h3 className="text-base font-bold text-slate-900 tracking-tight">{feat.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed font-normal">{feat.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default BifrostTab;
